import { useState, useRef, useEffect } from "react";
import { Button } from "@kaltura/ds-react-components";
import { Like24Icon } from "@kaltura/ds-react-icons";
import { baseUrl, postKmsData, translatePlural, translate } from "@kaltura/mediaspace-shared-utils";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import { SpanSrOnly } from "@kaltura/mediaspace-shared-ui";

export interface LikeProps {
    likesCount: number;
    isLiked: boolean;
    onToggle: (like: boolean) => void | Promise<void>;
    processing?: boolean;
}

export const Like = ({ likesCount, isLiked, onToggle, processing }: LikeProps) => {
    // State for screen reader announcements (only updated on user action)
    const [announcement, setAnnouncement] = useState("");

    // Track when user clicks to restore focus if needed
    const [shouldRestoreFocus, setShouldRestoreFocus] = useState(false);
    const buttonRef = useRef<HTMLButtonElement>(null);

    // Compute aria-label based on current state
    const likesText = translatePlural("%1 like", "%1 likes", [likesCount]);
    const ariaLabel = isLiked
        ? translate("Click to unlike media. Currently %1", [likesText])
        : translate("Click to like media. Currently %1", [likesText]);

    const handleClick = () => {
        const newState = !isLiked;

        // Mark that we should restore focus after re-render
        setShouldRestoreFocus(true);

        // Compute what the new count will be (optimistic)
        const newCount = likesCount + (newState ? 1 : -1);
        const newLikesText = translatePlural("%1 like", "%1 likes", [newCount]);
        const actionText = newState ? translate("liked") : translate("unliked");

        // Update announcement for screen readers
        setAnnouncement(translate("You %1 the media. Currently %2.", [actionText, newLikesText]));

        // Call the parent's toggle handler
        onToggle(newState);
    };

    // Restore focus after state changes from user click
    useEffect(() => {
        if (shouldRestoreFocus && buttonRef.current) {
            requestAnimationFrame(() => {
                buttonRef.current?.focus();
            });
            setShouldRestoreFocus(false);
        }
    }, [shouldRestoreFocus, isLiked, likesCount]);

    return (
        <>
            <Button
                ref={buttonRef}
                variant={"borderless"}
                size={"medium"}
                color={"secondary"}
                disabled={processing}
                onClick={handleClick}
                startIcon={<Like24Icon color={isLiked ? "primary" : "inherit"} />}
                title={likesText}
                aria-label={ariaLabel}
                aria-pressed={isLiked}
            >
                {likesCount}
            </Button>
            {/* Visually hidden aria-live region for announcements */}
            <SpanSrOnly role="status" aria-live="polite" aria-atomic="true">
                {announcement}
            </SpanSrOnly>
        </>
    );
};

export interface LikeContainerProps {
    likesCount: number;
    isLiked: boolean;
    entryId: string;
    categoryId?: string;
    playlistId?: string;
}

/**
 * like button container
 */
export const LikeContainer = ({
    likesCount: likesCountParam,
    isLiked: isLikedParam,
    entryId,
    categoryId,
    playlistId,
}: LikeContainerProps) => {
    const [likesCount, setLikesCount] = useState(likesCountParam);
    const [isLiked, setIsLiked] = useState(isLikedParam);
    const [processing, setProcessing] = useState(false);

    const sendButtonAnalytics = useButtonAnalytics();

    const onToggle = async (isLike: boolean) => {
        if (processing || isLike === isLiked) {
            return;
        }

        setIsLiked(isLike);
        // Change the likes count naively before getting up-to-date info from the server
        setLikesCount(likesCount + (isLike ? 1 : -1));

        setProcessing(true);
        // Update the like on the server, and get the updated likes count
        const url = baseUrl + "/default/media/like";
        const params = { id: entryId, categoryid: categoryId, playlistid: playlistId, isLike };
        try {
            const { likes } = await postKmsData(url, params);
            setLikesCount(likes);
        }
        catch (e: unknown) {
            // TODO: visual indication of the error?
            console.error("Failed to like/unlike a media", e, { url, params });
            // Restore previous values on API failure
            setIsLiked(isLiked);
            setLikesCount(likesCount);
        }
        setProcessing(false);

        // send analytics
        sendButtonAnalytics(`Like - ${isLiked ? "On" : "Off"}`, ButtonClickAnalyticsType.TOGGLE);
    };

    return <Like likesCount={likesCount} isLiked={isLiked} onToggle={onToggle} processing={processing} />;
};
